import type { Tone } from "@vixio/ui";

export type Stage =
  | "conteudo" | "interesse_qualificado" | "conversa_20min" | "diagnostico_leve" | "proposta"
  | "piloto_contrato" | "entrega_medida" | "caso" | "recomendacao" | "won" | "lost";

export const PIPELINE: ReadonlyArray<{ stage: Stage; label: string; criterion: string }> = [
  { stage: "conteudo", label: "Conteúdo", criterion: "Toda oportunidade nasce aqui." },
  { stage: "interesse_qualificado", label: "Interesse qualificado", criterion: "Quem decide ou influencia identificado." },
  { stage: "conversa_20min", label: "Conversa de 20 min", criterion: "A pessoa descreveu uma dor." },
  { stage: "diagnostico_leve", label: "Diagnóstico leve", criterion: "Dor com custo estimado." },
  { stage: "proposta", label: "Proposta", criterion: "Decisor confirmado." },
  { stage: "piloto_contrato", label: "Piloto ou contrato", criterion: "Proposta aceita (assinatura)." },
  { stage: "entrega_medida", label: "Entrega medida", criterion: "Nota com o marco da fase 1 entregue." },
  { stage: "caso", label: "Caso", criterion: "Nota com o resultado documentado." },
  { stage: "recomendacao", label: "Recomendação", criterion: "Nota com a publicação do caso." },
];

export const STAGE_LABEL: Record<Stage, string> = {
  ...(Object.fromEntries(PIPELINE.map((p) => [p.stage, p.label])) as Record<Stage, string>),
  won: "Ganha",
  lost: "Perdida",
};

export function stageIndex(s: Stage): number {
  return PIPELINE.findIndex((p) => p.stage === s);
}

export const ORIGINS = {
  rede_quente: "Rede quente", img: "Diagnóstico IMG", instagram: "Instagram", linkedin: "LinkedIn", indicacao: "Indicação",
  sebrae: "Sebrae", inpes: "InPES", palestra: "Palestra", site: "Site", outro: "Outro",
} as const;
export const SEGMENTS = {
  cliente_vixio: "Cliente Vixio", medico_dono: "Médico dono", ex_aluno_gestor: "Ex-aluno gestor", secretaria: "Secretaria",
  empresa: "Empresa", hospital: "Hospital", clinica: "Clínica", publico: "Setor público", outro: "Outro",
} as const;
export const PROPOSAL_KINDS = {
  diagnostico: "Diagnóstico", palestra: "Palestra", workshop: "Workshop", programa: "Programa", recorrente: "Recorrente",
  projeto: "Projeto", outro: "Outro",
} as const;
export const NEXT_STEPS = {
  diagnostico_pago: "Diagnóstico pago", briefing_tecnico: "Briefing técnico", encaminhamento: "Encaminhamento", encerramento: "Encerramento",
} as const;

export type ProposalStatus = "draft" | "sent" | "accepted" | "rejected" | "expired" | "superseded";
export const PROPOSAL_STATUS: Record<ProposalStatus, { label: string; tone: Tone }> = {
  draft: { label: "rascunho", tone: "neutral" },
  sent: { label: "enviada", tone: "info" },
  accepted: { label: "aceita", tone: "ok" },
  rejected: { label: "recusada", tone: "crit" },
  expired: { label: "vencida", tone: "warn" },
  superseded: { label: "substituída", tone: "neutral" },
};

export type OptionKey = "essencial" | "recomendada" | "completa";
export const OPTION_KEYS: OptionKey[] = ["essencial", "recomendada", "completa"];
export const OPTION_LABEL: Record<OptionKey, string> = { essencial: "Essencial", recomendada: "Recomendada", completa: "Completa" };

export interface Opportunity {
  id: string;
  org_id: string;
  person_id: string | null;
  /** Na view `sales.v_opportunities`: nome da empresa do core (`core.accounts`) quando a pessoa está ligada a uma; senão, o texto digitado. */
  account_name: string | null;
  /** Só em `sales.v_opportunities`: o texto digitado na oportunidade, a empresa do core e o CNPJ/CPF dela. */
  account_name_typed?: string | null;
  account_id?: string | null;
  account_doc?: string | null;
  hub_lead_id: string | null;
  origin: keyof typeof ORIGINS;
  segment: keyof typeof SEGMENTS;
  stage: Stage;
  lost_reason: string | null;
  pain: string | null;
  pain_cost_estimate_cents: number | null;
  decision_maker: string | null;
  decision_maker_confirmed: boolean;
  budget_cents: number | null;
  deadline: string | null;
  exact_objection: string | null;
  next_action: string | null;
  next_action_on: string | null;
  owner_user: string | null;
  stage_changed_at: string;
  closed_at: string | null;
  context: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface Proposal {
  id: string;
  org_id: string;
  opportunity_id: string;
  number: string;
  version: number;
  previous_id: string | null;
  kind: keyof typeof PROPOSAL_KINDS;
  title: string;
  problem: string;
  interlocutor: string;
  scope: string;
  not_included: string | null;
  acceptance_criteria: string | null;
  case_use_clause: string | null;
  isento_clause: string | null;
  pain_cost_calc: { hours_lost_per_month: number; hour_value_cents: number; months: number } | null;
  pain_cost_cents: number | null;
  price_floor_cents: number | null;
  price_floor_calc: Record<string, unknown> | null;
  status: ProposalStatus;
  valid_until: string | null;
  sent_at: string | null;
  decided_at: string | null;
  public_token: string | null;
  created_at: string;
}

export interface ProposalOption {
  id: string;
  proposal_id: string;
  key: OptionKey;
  offer_slug: string;
  title: string;
  description: string | null;
  price_cents: number;
  discount_cents: number;
  contrapartida: string | null;
  billing_period: "once" | "monthly";
  term_months: number | null;
  total_cents: number;
  below_floor_override: boolean;
  override_reason: string | null;
  override_floor_cents: number | null;
}

export interface OptionInput {
  key: OptionKey;
  offer_slug: string;
  title?: string;
  description?: string;
  price_cents: number;
  discount_cents?: number;
  contrapartida?: string;
  billing_period?: "once" | "monthly";
  term_months?: number | null;
  override_reason?: string;
}

export interface PublicProposal {
  organization: string;
  number: string;
  version: number;
  title?: string;
  status: ProposalStatus;
  problem?: string;
  interlocutor?: string;
  scope?: string;
  not_included?: string | null;
  acceptance_criteria?: string | null;
  case_use_clause?: string | null;
  isento_clause?: string | null;
  pain_cost?: { hours_lost_per_month: number; hour_value_cents: number; months: number; pain_cost_cents: number } | null;
  valid_until?: string;
  sent_at?: string;
  decided_at?: string | null;
  accepted_option?: OptionKey | null;
  options?: Array<{
    key: OptionKey; title: string; description: string | null; price_cents: number; discount_cents: number; contrapartida: string | null;
    net_price_cents: number; billing_period: "once" | "monthly"; term_months: number | null; total_cents: number;
  }>;
}

export interface Weekly {
  org_id: string;
  week_start: string;
  iso_week: string;
  conversas_qualificadas: number;
  propostas_enviadas: number;
  valor_em_negociacao_cents: number;
  valor_proposto_cents: number;
  propostas_aceitas: number;
  receita_contratada_cents: number;
  taxa_conversao: number | null;
}

export interface DueFollowup {
  followup_id: string;
  opportunity_id: string;
  proposal_id: string;
  proposal_number: string;
  step: number;
  template_key: string;
  due_on: string;
  person_name: string | null;
  account_name: string | null;
  message: string;
}

type U = string;
declare module "@vixio/data" {
  interface RpcRegistry {
    "sales.create_opportunity": {
      args: {
        p_org: U; p_person?: U | null; p_account_name?: string | null; p_origin?: string; p_segment?: string; p_pain?: string | null;
        p_pain_cost_estimate_cents?: number | null; p_decision_maker?: string | null; p_decision_maker_confirmed?: boolean;
        p_budget_cents?: number | null; p_deadline?: string | null; p_next_action?: string | null; p_next_action_on?: string | null;
        p_owner_user?: U | null;
      };
      returns: U;
    };
    "sales.update_opportunity": { args: { p_opportunity: U; p_patch: Partial<Opportunity> }; returns: null };
    "sales.advance": { args: { p_opportunity: U; p_to_stage: Stage; p_note?: string | null }; returns: null };
    "sales.add_note": { args: { p_opportunity: U; p_body: string }; returns: null };
    "sales.record_conversation": {
      args: {
        p_opportunity: U; p_motivo: string; p_exemplo_volume: string; p_sistemas_custos: string; p_decisor_orcamento_prazo: string;
        p_proximo_passo: string; p_notes?: string | null; p_held_on?: string | null;
      };
      returns: U;
    };
    "sales.install_defaults": { args: { p_org: U }; returns: number };
    "sales.create_proposal": {
      args: {
        p_opportunity: U; p_title: string; p_problem: string; p_interlocutor: string; p_scope: string; p_kind?: string;
        p_not_included?: string | null; p_acceptance_criteria?: string | null; p_case_use_clause?: string | null;
        p_isento_clause?: string | null; p_valid_until?: string | null;
      };
      returns: U;
    };
    "sales.update_proposal": { args: { p_proposal: U; p_patch: Partial<Proposal> }; returns: null };
    "sales.compute_pain_cost": { args: { p_proposal: U; p_hours_lost_per_month: number; p_hour_value_cents: number; p_months: number }; returns: number };
    "sales.set_price_floor": { args: { p_proposal: U; p_inputs: unknown }; returns: number };
    "sales.set_options": { args: { p_proposal: U; p_options: OptionInput[] }; returns: number };
    "sales.send_proposal": { args: { p_proposal: U; p_valid_until?: string | null }; returns: { proposal_id: U; number: string; version: number; public_token: string } };
    "sales.new_version": { args: { p_proposal: U }; returns: U };
    "sales.record_acceptance": { args: { p_proposal: U; p_option_key: OptionKey; p_name: string; p_email?: string | null }; returns: unknown };
    "sales.record_rejection": { args: { p_proposal: U; p_reason: string }; returns: unknown };
    "sales.due_followups": { args: { p_org: U; p_date?: string | null }; returns: DueFollowup[] };
    "sales.complete_followup": { args: { p_followup: U; p_outcome: "enviado" | "respondeu" }; returns: null };
    "sales.get_public_proposal": { args: { p_token: string }; returns: PublicProposal };
    "sales.accept_public": { args: { p_token: string; p_option_key: OptionKey; p_name: string; p_email: string }; returns: { status: string; number: string; option: OptionKey } };
    "sales.reject_public": { args: { p_token: string; p_reason?: string | null }; returns: { status: string } };
    "sales.pain_cost": { args: { p_hours_lost_per_month: number; p_hour_value_cents: number; p_months: number }; returns: number };
  }
}

export function opportunityTitle(o: Pick<Opportunity, "account_name" | "person_id">, personName?: string | null): string {
  return o.account_name || personName || "Oportunidade sem nome";
}
